import { FC, useCallback, useContext, useMemo, useState } from "react";
import {
    SportFavoritesContext,
    LiveFeedContext,
    MultiViewContext,
    useSiteConfig,
} from "@finbackoffice/site-core";
import Loading from "components/base/loading/Loading";
import { ISportFeedResponse } from "@finbackoffice/websocket-client";
import Button from "components/base/button/Button";
import { Svg } from "components/base/svg/Svg";
import { RouteNames } from "utils/constants";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import AdvancedSidebarLiveSport from "components/side-bar/advanced/live/sport/AdvancedSidebarLiveSport";
import Popover from "components/base/popover/Popover";
import { useDefaultLiveParamsFromUrl } from "hooks";
import Sport from "components/sport/base/sport/Sport";
import ActiveLink from "components/base/active-link/ActiveLink";
import Translate from "components/base/translate/Translate";
import { AssetsDirectory } from "@finbackoffice/enums";
import { MainSidebarContext } from "../MainSidebar";
import styles from "./main-sidebar-live.module.sass";

const MainSidebarLive: FC = () => {
    const router = useRouter();
    const pathname = usePathname();
    const multiviewEnabled = useSiteConfig<boolean>("multiview");

    const { selectedSportId, setSelectedSportId, scrollRef } = useContext(MainSidebarContext);
    const { liveFeed, liveIsLoading, liveTopLeaguesGamesCount } = useContext(LiveFeedContext);
    const { favLiveLeaguesGameCount } = useContext(SportFavoritesContext);
    const { isSportSelected } = useContext(MultiViewContext) ?? {};

    const { sportSlug } = useDefaultLiveParamsFromUrl();

    const [popoverLastPosition, setPopoverLastPosition] = useState<{
        top: number | null;
        left: number | null;
    }>({ top: null, left: null });

    const gamesCount = favLiveLeaguesGameCount + liveTopLeaguesGamesCount;

    const handleClose = useCallback(() => {
        setSelectedSportId(null);
    }, [setSelectedSportId]);

    const handleSetSelectedSport = useCallback(
        (id: number) => {
            if (id === selectedSportId) {
                handleClose();
            } else {
                setSelectedSportId(id);
            }
        },
        [handleClose, selectedSportId, setSelectedSportId],
    );

    const activeSportSlug = useMemo(() => {
        if (pathname.includes(`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`)) {
            return sportSlug ?? liveFeed[0]?.slug;
        }

        return sportSlug;
    }, [liveFeed, sportSlug, pathname]);

    return (
        <>
            <div className={styles.subNav}>
                <Button
                    type="custom"
                    disabled={gamesCount === 0}
                    className={classNames(styles.subNavItem, {
                        [styles.active]: pathname?.includes(RouteNames.TOP),
                    })}
                    onClick={() =>
                        router.push(`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.TOP}`)
                    }>
                    <>
                        <span className={styles.gamesCount}>{gamesCount}</span>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/favorite-events.svg`}
                            className="svg-icon"
                        />
                        <Translate tid="sport_favorite_events" />
                    </>
                </Button>
                {multiviewEnabled && (
                    <ActiveLink
                        href={`/${RouteNames.SPORT}/${RouteNames.MULTI_VIEW}`}
                        activeClassName={styles.active}
                        className={classNames(styles.subNavItem)}>
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Sidebar}/multiview.svg`}
                                className="svg-icon"
                            />
                            <span>
                                <Translate tid="pageName_multiView" />
                            </span>
                        </>
                    </ActiveLink>
                )}
            </div>
            <div className={styles.subNav}>
                {!liveIsLoading ? (
                    <>
                        {liveFeed.map((sport: ISportFeedResponse) => (
                            <Popover
                                key={sport.id}
                                scrollRef={scrollRef}
                                scrollBehavior="close"
                                contentClassName={styles.popoverContent}
                                placement="right"
                                initialPosition={popoverLastPosition}
                                onPositionChange={(position) => {
                                    setPopoverLastPosition(position);
                                }}
                                onOpen={() => handleSetSelectedSport(sport.id)}
                                onOutsideClick={(event) => {
                                    const target = event?.target as Element;
                                    if (!target?.closest(".popover-trigger-container")) {
                                        setPopoverLastPosition({ top: null, left: null });
                                    }
                                    handleClose();
                                }}
                                trigger={
                                    <Sport
                                        key={sport.id}
                                        sport={sport}
                                        hasFavorite={false}
                                        variant="sidebar"
                                        type="button"
                                        isSelected={
                                            activeSportSlug === sport.slug ||
                                            isSportSelected(sport.id)
                                        }
                                    />
                                }>
                                <AdvancedSidebarLiveSport
                                    sport={sport}
                                    openedByDefault
                                    disableCollapsing
                                />
                            </Popover>
                        ))}
                    </>
                ) : (
                    <Loading />
                )}
            </div>
        </>
    );
};

export default MainSidebarLive;
